import React from 'react';

import { useLocation } from 'react-router';

import { appRoutes } from '@clearverify/constants';

import { FullscreenLoader } from 'src/components/common';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import { getIsPathMatchLocation } from 'src/utils/router';

import { AuthLayout } from './AuthLayout';
import { ClearVerifyAdminLayout } from './ClearVerifyAdminLayout';
import { ClearVerifyManagerLayout } from './ClearVerifyManagerLayout';
import { ConsumerLayout } from './ConsumerLayout';
import { EmployerAdminLayout } from './EmployerAdminLayout';
import { EmployerManagerLayout } from './EmployerManagerLayout';
import { PEOAdminLayout } from './PEOAdminLayout';
import { PEOManagerLayout } from './PEOManagerLayout';
import { PEOPayrollProviderLayout } from './PEOPayrollProviderLayout';
import { VerifierAdminLayout } from './VerifierAdminLayout';
import { VerifierManagerLayout } from './VerifierManagerLayout';

export const LayoutResolver: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation();

  const { isUserLoading } = useCurrentUser();

  const {
    isPlatformAdmin,
    isPlatformManager,
    isEmployerAdmin,
    isEmployerManager,
    isVerifierAdmin,
    isVerifierManager,
    isEmployee,
    isPeoSuperAdmin,
    isPeoAdmin,
    isPeoManager,
    isPeoPayrollProviderUser,
  } = useCurrentUserPermissions();

  // scroll to top on routing
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isAuthLayoutRoute = React.useMemo(
    () =>
      getIsPathMatchLocation(location, appRoutes.auth.index) ||
      getIsPathMatchLocation(location, appRoutes.maintenance.index),
    [location],
  );

  if (isUserLoading) {
    return <FullscreenLoader />;
  }

  if (isAuthLayoutRoute) {
    return <AuthLayout>{children}</AuthLayout>;
  }

  if (isPeoPayrollProviderUser) {
    return <PEOPayrollProviderLayout>{children}</PEOPayrollProviderLayout>;
  }

  if (isPlatformAdmin) {
    return <ClearVerifyAdminLayout>{children}</ClearVerifyAdminLayout>;
  }

  if (isPlatformManager) {
    return <ClearVerifyManagerLayout>{children}</ClearVerifyManagerLayout>;
  }

  if (isEmployerAdmin) {
    return <EmployerAdminLayout>{children}</EmployerAdminLayout>;
  }

  if (isEmployerManager) {
    return <EmployerManagerLayout>{children}</EmployerManagerLayout>;
  }

  if (isVerifierAdmin) {
    return <VerifierAdminLayout>{children}</VerifierAdminLayout>;
  }

  if (isVerifierManager) {
    return <VerifierManagerLayout>{children}</VerifierManagerLayout>;
  }

  if (isEmployee) {
    return <ConsumerLayout>{children}</ConsumerLayout>;
  }

  if (isPeoSuperAdmin || isPeoAdmin) {
    return <PEOAdminLayout>{children}</PEOAdminLayout>;
  }

  if (isPeoManager) {
    return <PEOManagerLayout>{children}</PEOManagerLayout>;
  }

  return <React.Fragment>{children}</React.Fragment>;
};
